import { ClipboardCheck, FileText, Shield, UserRoundCheck } from "lucide-react";

import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";

const safeguardingPillars = [
  {
    title: "DBS checks",
    icon: Shield,
    body: "Driver suitability requirements form part of the safeguarding foundation behind each CareFleet journey.",
  },
  {
    title: "Driver training",
    icon: UserRoundCheck,
    body: "Structured expectations, secure workflows, and service discipline help maintain consistency in how journeys are delivered.",
  },
  {
    title: "Incident management",
    icon: FileText,
    body: "Events can be logged, reviewed, and followed up through a clearer record of what happened and when.",
  },
  {
    title: "Secure handovers",
    icon: ClipboardCheck,
    body: "Pickup and drop-off processes are reinforced through digital checkpoints that support safer passenger transitions.",
  },
];

export const SafeguardingPage = () => {
  return (
    <div>
      <PageHero
        eyebrow="Safeguarding & compliance"
        title="A transport model designed around structure, visibility, and documented control."
        description="CareFleet combines safeguarding-minded service delivery with digital processes that help make accountability clearer for operational teams, schools, councils, and families through structured visibility and controlled checkpoints."
        highlights={["DBS checks", "Training standards", "Incident records", "Secure handovers"]}
        actions={[{ label: "Request Demo", to: "/contact", testId: "safeguarding-request-demo-button" }]}
        baseTestId="safeguarding-hero"
        visual={
          <div className="image-shell">
            <img
              src="https://images.unsplash.com/photo-1732194439331-08ec61c4df4f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
              alt="Wheelchair accessible transport environment designed for safe handovers"
              className="aspect-[5/4] w-full object-cover object-center"
            />
          </div>
        }
      />

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-12">
          <SectionHeading
            eyebrow="Safeguarding alignment"
            title="Designed in alignment with UK safeguarding expectations and duty of care principles."
            description="CareFleet has been designed to support secure handovers, communication pathways, incident awareness and logging, and role-based visibility without making unsupported certification claims."
            testId="safeguarding-alignment-heading"
          />

          <div className="grid gap-6 lg:grid-cols-4">
            {[
              "Secure handover approach",
              "Communication pathways",
              "Incident awareness and logging",
              "Role-based visibility",
            ].map((item) => (
              <Reveal key={item}>
                <div className="soft-card h-full p-6" data-testid={`safeguarding-alignment-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <p className="font-medium text-slate-800">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <SectionHeading
            eyebrow="Trust signals"
            title="Safeguarding is not just policy language — it is built into how the journey is managed."
            description="Our approach is designed to show stakeholders how key safeguarding steps are supported by digital traceability and operational structure."
            testId="safeguarding-pillars-heading"
          />

          <div className="grid gap-6 lg:grid-cols-2">
            {safeguardingPillars.map((pillar) => {
              const Icon = pillar.icon;
              return (
                <Reveal key={pillar.title}>
                  <div className="soft-card h-full p-8" data-testid={`safeguarding-pillar-${pillar.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-mist text-brand-navy">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h2 className="mt-6 font-heading text-3xl font-semibold text-slate-950">{pillar.title}</h2>
                    <p className="mt-4 text-base leading-8 text-slate-600">{pillar.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};